import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Async, Empty, PageHead, Segmented, Status, useAction } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Submission } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

type Decision = "approved" | "rejected" | "duplicate";

export function SubmissionsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [status, setStatus] = useState<"pending" | "approved" | "rejected" | "duplicate" | "all">("pending");
	const state = useAsync(() => api.submissions({ status, limit: 100 }), [status]);
	const { run, busy } = useAction();
	/** The decision being taken, and on what. The note is written here, once. */
	const [asked, setAsked] = useState<{ s: Submission; decision: Decision } | null>(null);
	const [note, setNote] = useState("");

	function ask(s: Submission, decision: Decision) {
		setNote("");
		setAsked({ s, decision });
	}

	async function decide() {
		if (!asked) return;
		const { s, decision } = asked;
		const r = await run(
			() => api.decide(s.id, decision, note || undefined),
			(r) =>
				r.entity_id
					? t("submissions.approved", { id: r.entity_id })
					: t("submissions.decided", { status: t(`submissions.status.${decision}`) }),
		);
		if (r) {
			setAsked(null);
			state.reload();
		}
	}

	return (
		<>
			<PageHead title={t("submissions.title")} help={t("submissions.lede")} />
			<div className="table-tools">
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{ value: "pending", label: t("submissions.status.pending") },
						{ value: "approved", label: t("submissions.status.approved") },
						{ value: "rejected", label: t("submissions.status.rejected") },
						{ value: "duplicate", label: t("submissions.status.duplicate") },
						{ value: "all", label: t("state.all") },
					]}
				/>
			</div>
			<Async state={state}>
				{({ submissions }) =>
					submissions.length === 0 ? (
						<Empty />
					) : (
						<div className="table-scroll">
							<Table className="text-[length:var(--t-sm)]">
								<TableHeader>
									<TableRow>
										<TableHead>{t("submissions.kind")}</TableHead>
										<TableHead>{t("entities.name")}</TableHead>
										<TableHead>{t("submissions.from")}</TableHead>
										<TableHead>{t("state.status")}</TableHead>
										<TableHead>{t("state.created")}</TableHead>
										<TableHead>{t("state.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{submissions.map((s) => (
										<TableRow key={s.id}>
											<TableCell>
												<Status value={s.kind} prefix="submissions.kind" />
											</TableCell>
											<TableCell>
												<strong>{s.payload.name}</strong>
												<span className="sub">
													<a href={s.payload.website} target="_blank" rel="noreferrer">
														{s.payload.website}
													</a>
												</span>
												{s.payload.tagline ? <span className="sub">{s.payload.tagline}</span> : null}
												{s.payload.category ? (
													<span className="sub">
														{t("submissions.category")}: {s.payload.category}
													</span>
												) : null}
												{s.entity_id ? (
													<span className="sub">
														{t("submissions.existing")}:{" "}
														<Link to={`/entities/${s.entity_id}`}>{s.entity_name ?? s.entity_id}</Link>
													</span>
												) : null}
											</TableCell>
											<TableCell>{s.submitter_email ?? "—"}</TableCell>
											<TableCell>
												<Status value={s.status} prefix="submissions.status" />
												{s.note ? <span className="sub">{s.note}</span> : null}
											</TableCell>
											<TableCell>{fmt.dateTime(locale, s.created_at)}</TableCell>
											<TableCell>
												{s.status === "pending" ? (
													<div className="row-actions">
														<Button
															variant="default"
															size="sm"
															type="button"
															disabled={busy}
															onClick={() => ask(s, "approved")}
														>
															{t("submissions.approve")}
														</Button>
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	variant="default"
																	size="sm"
																	type="button"
																	disabled={busy}
																	aria-label={t("state.moreActions")}
																>
																	<MoreHorizontal />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuItem onSelect={() => ask(s, "duplicate")}>
																	{t("submissions.duplicate")}
																</DropdownMenuItem>
																<DropdownMenuItem
																	className="text-[var(--text-error)]"
																	onSelect={() => ask(s, "rejected")}
																>
																	{t("submissions.reject")}
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												) : (
													<span className="faint">{fmt.date(locale, s.reviewed_at)}</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)
				}
			</Async>
			{asked ? (
				<DecideModal
					asked={asked}
					note={note}
					setNote={setNote}
					busy={busy}
					onClose={() => setAsked(null)}
					onConfirm={decide}
				/>
			) : null}
		</>
	);
}

/**
 * Deciding a submission is one dialog, whichever way it goes. The note used to
 * be an input on every pending row, so the page carried as many forms as there
 * were applications and none of them said what pressing the button would do.
 */
function DecideModal({
	asked,
	note,
	setNote,
	busy,
	onClose,
	onConfirm,
}: {
	asked: { s: Submission; decision: Decision };
	note: string;
	setNote: (next: string) => void;
	busy: boolean;
	onClose: () => void;
	onConfirm: () => void;
}) {
	const t = useT();
	const { s, decision } = asked;
	const name = s.payload.name ?? s.entity_name ?? s.payload.website ?? s.id;
	const label = t(
		decision === "approved"
			? "submissions.approve"
			: decision === "rejected"
				? "submissions.reject"
				: "submissions.duplicate",
	);
	const consequence = t(
		decision === "approved"
			? "submissions.approveConfirm"
			: decision === "rejected"
				? "submissions.rejectConfirm"
				: "submissions.duplicateConfirm",
		{ name },
	);
	return (
		<ChipModal open dismissDisabled={busy} srTitle={label} onOpenChange={onClose}>
			<ChipModalHeader onClose={onClose}>{label}</ChipModalHeader>
			<ChipModalBody>
				<ChipModalField
					type="copy"
					title={t("submissions.entry")}
					value={[name, s.payload.website].filter(Boolean).join(" · ")}
				/>
				<ChipModalField type="input" title={t("state.note")} hint={consequence} value={note} onChange={setNote} />
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={t("state.cancel")}
				onCancel={onClose}
				defaultAction={decision === "approved" ? "primary" : "none"}
				primaryAction={{
					label,
					variant: decision === "approved" ? "primary" : "destructive",
					disabled: busy,
					onClick: onConfirm,
				}}
			/>
		</ChipModal>
	);
}
